import { PageLayout } from "@/components/PageLayout";

const LAST_UPDATED = "September 12, 2026";
const EFFECTIVE = "September 12, 2026";

const SUPPORT_EMAIL = "blitzwashlaundry@gmail.com";
const SUPPORT_PHONE = "+91 63673 15665";
const COMPANY = "Blitzcare Services Private Limited";
const CIN = "U96010RJ2026PTC114005";
const GRIEVANCE_OFFICER = "Keshav Parwal";
const ADDRESS_LINES = [
  "A-307, Vidhyut Nagar, Ajmer Road,",
  "Vaishali Nagar, Jaipur \u2013 302021,",
  "Rajasthan, India",
];

// Each section renders in order. Supported optional keys:
//   body: string[]        -> paragraphs
//   list: string[]        -> bulleted list
//   table: { head: [string, string], rows: [string, string][] }
//   note: string[]        -> highlighted callout paragraphs
const sections = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: [
      "Blitzwash is a 24-hour laundry, dry-cleaning and garment-care service operating in Jaipur, Rajasthan. The Blitzwash mobile app and website are owned and operated by Blitzcare Services Private Limited (CIN U96010RJ2026PTC114005), a company registered in India with its registered office at A-307, Vidhyut Nagar, Ajmer Road, Vaishali Nagar, Jaipur \u2013 302021, Rajasthan.",
      "In this policy, \u201cwe\u201d, \u201cus\u201d and \u201cBlitzwash\u201d mean Blitzcare Services Private Limited. \u201cYou\u201d means anyone who uses the Blitzwash app or website to book, track or pay for our services.",
      "We act as the data fiduciary for the personal data described here, under India\u2019s Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000 and the rules made under it.",
    ],
  },
  {
    id: "what-we-collect",
    title: "2. Data we collect",
    body: [
      "We only collect what we need to pick up your clothes, clean them, and get them back to you.",
    ],
    table: {
      head: ["What", "Why we need it"],
      rows: [
        ["Name and mobile number", "To create your account, verify it by OTP, identify your order, and let our pickup rider call you."],
        ["Email address (optional)", "To send invoices and order receipts if you prefer email."],
        ["Pickup and delivery addresses", "To collect and return your garments at the right door, and to check the address falls inside our service area."],
        ["Approximate or precise location", "Only when you tap to auto-fill an address or to find your nearest Blitzwash store. You can always type the address instead."],
        ["Order details", "Items, garment tags and QR codes, service type, fabric notes, stain notes, special instructions, prices, discounts and order status. This is how we track each garment through inspection, cleaning, sorting and packing."],
        ["Photos you upload (optional)", "Only if you photograph a stain, a damaged item or a delivery instruction. We use it to advise on treatment and to record the garment\u2019s condition."],
        ["Payment information", "Amount, status, payment method type and a transaction reference. Card, UPI and bank details are entered on our payment partner\u2019s secure screen and are never stored on our servers. See section 7."],
        ["Device and app information", "Device model, operating system version, app version, language, a randomly generated app instance ID and crash logs. Used to fix bugs and keep the app stable."],
        ["Push notification token", "To tell you when a rider is on the way, an order is ready, or a delivery is out for drop-off."],
        ["Support messages", "Anything you send us by in-app chat, WhatsApp, email or phone, so we can resolve your issue and keep a record of it."],
      ],
    },
    bodyAfter: [
      "You sign in to Blitzwash using your mobile number and a one-time password. We do not offer Google, Facebook or email-and-password sign-in, so we never receive a social profile, a profile photo or a password from you.",
      "We do not collect your contacts list, SMS messages, call logs, browsing history outside our app, biometric data, or any government identification number. We do not ask for or process any special category of sensitive personal data beyond what is listed above.",
    ],
  },
  {
    id: "permissions",
    title: "3. Device permissions",
    body: [
      "The app asks for permissions only at the moment a feature needs them, and it works without most of them. You can withdraw any permission at any time in your device settings.",
    ],
    list: [
      "Location \u2014 to auto-detect your pickup address and show your nearest store. Only while the app is open. We do not track your location in the background.",
      "Camera \u2014 to photograph a stain or garment for a service query, or to scan a garment QR tag.",
      "Photos and media \u2014 to attach an existing image to a service query. We only read the file you pick.",
      "Notifications \u2014 to send order and delivery updates.",
      "Phone \u2014 to dial our support number or your assigned rider directly from the app. We never place calls on our own.",
    ],
    bodyAfter: [
      "Our delivery riders use a separate Blitzwash rider application, which this policy does not cover. If you see a rider\u2019s position moving on a map, that location comes from the rider\u2019s own device \u2014 never from yours.",
    ],
  },
  {
    id: "why",
    title: "4. Why we use your data",
    list: [
      "To register you, verify your mobile number and keep your account secure.",
      "To schedule pickups, process your garments, and complete deliveries.",
      "To track individual garments through our cleaning process so nothing is lost or mixed up.",
      "To take payment, issue invoices, process refunds and maintain statutory accounting and GST records.",
      "To notify you about the status of your order.",
      "To answer your questions and resolve complaints.",
      "To detect fraud, misuse of promotional offers, and abuse of our service.",
      "To understand which features are used and improve the app.",
      "To send you offers and service updates \u2014 only where you have opted in, and you can opt out at any time.",
      "To meet legal, tax and regulatory obligations in India.",
    ],
    note: [
      "We do not sell your personal data. We do not share it with advertising networks or data brokers, and we do not use your data to build profiles for third-party advertising.",
    ],
  },
  {
    id: "legal-basis",
    title: "5. Consent and legal basis",
    body: [
      "We process your data on the basis of the consent you give when you create an account and place an order, and for the \u201clegitimate uses\u201d permitted under the Digital Personal Data Protection Act, 2023 \u2014 including fulfilling a service you have asked for and complying with the law.",
      "You may withdraw your consent at any time by writing to us at blitzwashlaundry@gmail.com. Withdrawing consent does not affect anything we did lawfully before you withdrew it, and we may still need to keep certain records to meet tax and accounting requirements. If you withdraw consent for data we need to serve you, we may not be able to continue providing the service.",
    ],
  },
  {
    id: "sharing",
    title: "6. Who we share it with",
    body: [
      "We share the minimum necessary data with the following categories of recipients, each bound by contract to use it only for the purpose we specify:",
    ],
    list: [
      "Our delivery riders and store staff \u2014 your name, phone number, address and order details, so they can collect and deliver your garments.",
      "Payment gateways \u2014 to process your payment securely. See section 7.",
      "Cloud hosting and database providers \u2014 to store the app\u2019s data on secure servers.",
      "Messaging providers \u2014 to deliver OTPs, order updates and receipts over SMS, WhatsApp or email.",
      "Analytics and crash reporting providers \u2014 to receive anonymised or pseudonymised app usage and crash data.",
      "Professional advisers and auditors \u2014 where needed for accounting, tax or legal compliance.",
      "Government authorities and courts \u2014 where we are required by law to disclose information, or to establish, exercise or defend a legal claim.",
    ],
    bodyAfter: [
      "If our business is merged, acquired or restructured, your data may transfer to the successor entity, which will remain bound by this policy or a policy at least as protective. We will tell you if that happens.",
      "Your data is stored and processed in India. If any service provider processes data outside India, we ensure the transfer is permitted under applicable Indian law and protected by appropriate contractual safeguards.",
    ],
  },
  {
    id: "payments",
    title: "7. Payments",
    body: [
      "Online payments in the Blitzwash app are processed by Razorpay Software Private Limited, a payment aggregator authorised by the Reserve Bank of India. When you pay, you are taken to Razorpay\u2019s secure, PCI-DSS compliant screen.",
      "Your card number, CVV, UPI PIN, net-banking credentials and wallet passwords are entered directly with Razorpay. Blitzwash never sees, receives or stores them. We only receive confirmation of the amount, the payment status, the method type and a transaction reference, which we use to reconcile your order.",
    ],
    link: {
      text: "Razorpay processes your payment data under its own privacy policy, available at",
      label: "razorpay.com/privacy",
      href: "https://razorpay.com/privacy/",
    },
  },
  {
    id: "retention",
    title: "8. How long we keep it",
    list: [
      "Account details \u2014 for as long as your account is active, and up to 90 days after you ask us to delete it.",
      "Order and garment records \u2014 for as long as needed to handle disputes and claims about a garment.",
      "Invoices and payment records \u2014 for at least 8 years, as required by Indian tax, GST and company law. These cannot be deleted on request.",
      "Photos you upload \u2014 30 days from the date you upload them, after which they are permanently deleted.",
      "Support conversations \u2014 up to 3 years from the date the issue is closed.",
      "Crash and diagnostic logs \u2014 up to 12 months.",
    ],
    bodyAfter: [
      "When a retention period ends, we delete the data or irreversibly anonymise it so it can no longer identify you.",
    ],
  },
  {
    id: "security",
    title: "9. How we protect it",
    body: [
      "We use reasonable security practices appropriate to the sensitivity of the data, including encryption in transit over HTTPS/TLS, encrypted storage of credentials, OTP-based login, role-based access so staff only see what their job requires, audit logging of access to customer records, and regular review of our access controls.",
      "No system is perfectly secure. If a personal data breach occurs that is likely to affect you, we will notify you and the Data Protection Board of India as required by law, and tell you what happened and what to do.",
    ],
  },
  {
    id: "your-rights",
    title: "10. Your rights",
    body: [
      "Under the Digital Personal Data Protection Act, 2023, you have the right to:",
    ],
    list: [
      "Access a summary of the personal data we hold about you and how we process it.",
      "Correct or update data that is inaccurate, incomplete or out of date. Most of this you can do yourself in the app\u2019s profile screen.",
      "Erase your data where we no longer need it for the purpose it was collected or to meet a legal obligation.",
      "Withdraw consent at any time, as described in section 5.",
      "Nominate another person to exercise your rights in the event of your death or incapacity.",
      "Complain to us, and then to the Data Protection Board of India if you are not satisfied with our response.",
    ],
    bodyAfter: [
      "To exercise any of these, write to our Grievance Officer at the address in section 15. We respond within 30 days, and usually much sooner. We may ask you to verify your identity before we act, to make sure we are not giving your data to someone else.",
    ],
  },
  {
    id: "delete",
    title: "11. Deleting your account",
    body: [
      "You can delete your Blitzwash account and your personal data at any time:",
    ],
    list: [
      "In the app \u2014 go to Profile \u2192 Account settings \u2192 Delete account.",
      "On the web \u2014 submit a request at blitzwash.in/delete-account.html.",
      "By email or phone \u2014 write to blitzwashlaundry@gmail.com, or call +91 63673 15665 from your registered mobile number.",
    ],
    bodyAfter: [
      "When you delete your account we remove your name, phone number, email, saved addresses, uploaded photos and notification token within 30 days, and any backup copies within 90 days. Invoices and payment records are retained in de-identified form for the statutory period described in section 8. Once deleted, your account and order history cannot be restored.",
    ],
  },
  {
    id: "children",
    title: "12. Children",
    body: [
      "Blitzwash is not intended for children under 18. We do not knowingly create accounts for, or collect personal data from, anyone under 18. If you believe a child has given us personal data, write to us and we will delete it promptly.",
    ],
  },
  {
    id: "cookies",
    title: "13. Cookies and analytics",
    body: [
      "Our website uses cookies and similar technologies to keep you signed in, remember your preferences, and measure how the site is used. Our app uses an equivalent app instance identifier rather than cookies.",
      "We use Google Tag Manager and Google Analytics on the website, and an analytics and crash-reporting toolkit inside the app, to understand aggregate usage \u2014 screens viewed, features used, where people drop off, and what caused a crash. This data is pseudonymised and we do not use it to identify you personally, and we do not pass it to advertising networks.",
    ],
    link: {
      text: "You can block cookies in your browser settings, though parts of the site may then stop working. You can read how Google handles this data at",
      label: "policies.google.com/technologies/partner-sites",
      href: "https://policies.google.com/technologies/partner-sites",
    },
  },
  {
    id: "changes",
    title: "14. Changes to this policy",
    body: [
      "We update this policy when our practices or the law change. The \u201clast updated\u201d date at the top always reflects the current version. If a change materially affects how we use your data, we will tell you in the app or by SMS or email before it takes effect, and where the law requires it, ask for your consent again. Continuing to use Blitzwash after a change means you accept the updated policy.",
    ],
  },
];

function SectionBlock({ section }) {
  return (
    <div id={section.id} className="scroll-mt-28">
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">
        {section.title}
      </h2>

      {section.body?.map((paragraph, j) => (
        <p key={`b-${j}`} className="text-slate-600 leading-relaxed mb-3">
          {paragraph}
        </p>
      ))}

      {section.table && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 mt-2 mb-4">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50">
                {section.table.head.map((h, k) => (
                  <th
                    key={k}
                    className="px-4 py-3 font-semibold text-slate-900 border-b border-slate-200 align-top"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row, r) => (
                <tr key={r} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-semibold text-slate-800 align-top w-1/3">
                    {row[0]}
                  </td>
                  <td className="px-4 py-3 text-slate-600 leading-relaxed align-top">
                    {row[1]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section.list && (
        <ul className="list-disc pl-6 space-y-2 mt-2 mb-3">
          {section.list.map((item, k) => (
            <li key={k} className="text-slate-600 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      )}

      {section.note && (
        <div className="rounded-2xl border-l-4 border-sky-400 bg-sky-50/70 p-4 my-2">
          {section.note.map((n, k) => (
            <p key={k} className="text-slate-700 leading-relaxed last:mb-0 mb-2">
              {n}
            </p>
          ))}
        </div>
      )}

      {section.link && (
        <p className="text-slate-600 leading-relaxed mb-3">
          {section.link.text}{" "}
          <a
            href={section.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-sky-600 hover:text-sky-700 transition-colors"
          >
            {section.link.label}
          </a>
          .
        </p>
      )}

      {section.bodyAfter?.map((paragraph, j) => (
        <p key={`a-${j}`} className="text-slate-600 leading-relaxed mb-3">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <PageLayout>
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-10 bg-gradient-to-br from-sky-50/60 via-white to-lime-50/40 text-center">
        <div className="max-w-3xl mx-auto px-5">
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">
            Legal
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-500 leading-relaxed">
            This policy explains what the Blitzwash app and website collect from you,
            why we need it, who we share it with, and the choices you have over it.
          </p>
          <p className="text-sm text-slate-400 mt-4">
            Last updated: {LAST_UPDATED} &middot; Effective: {EFFECTIVE}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Applies to Blitzwash: Dry Clean &amp; Laundry (Android &amp; iOS) and blitzwash.in
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 space-y-10">
          {sections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          {/* Contact */}
          <div id="contact" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">
              15. Contact us
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              For any question, request or complaint about your personal data, contact
              our Grievance Officer, appointed under the Information Technology Act, 2000
              and the Digital Personal Data Protection Act, 2023:
            </p>
            <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-6 space-y-3">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Grievance Officer</p>
                <p className="text-sm font-semibold text-slate-800">{GRIEVANCE_OFFICER}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Company</p>
                <p className="text-sm text-slate-700">{COMPANY} (Blitzwash)</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Email</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Phone</p>
                <a
                  href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
                  className="text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  {SUPPORT_PHONE}
                </a>
                <p className="text-sm text-slate-600 mt-1">Monday to Sunday, 9:00 am \u2013 6:00 pm IST</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Address</p>
                <p className="text-sm text-slate-700">
                  {ADDRESS_LINES.map((line, i) => (
                    <span key={i}>
                      {line}
                      {i < ADDRESS_LINES.length - 1 && <br />}
                    </span>
                  ))}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">CIN</p>
                <p className="text-sm text-slate-700">{CIN}</p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mt-4">
              We acknowledge every request within 48 hours and resolve it within 30 days.
              If you are not satisfied with our response, you may escalate your complaint to
              the Data Protection Board of India.
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
